import React, { useState } from "react";
import { confirmAlert } from 'components/common';
import { useDispatch } from "react-redux";
import { MdFlag, MdFormatListBulleted } from "react-icons/md";
import { FaPlus, FaEdit, FaTrashAlt } from "react-icons/fa";
import CrmListPage from "components/app/crm/shared/CrmListPage";
import FeatureFlagForm from "components/app/authorization/feature-flag/FeatureFlagForm";
import { getAllFeatureFlags, deleteFeatureFlag } from "redux/authorization/featureFlagSlice";

const STAGE_BADGE = {
  hidden:   "tw-bg-slate-100 tw-text-slate-600",
  internal: "tw-bg-amber-100 tw-text-amber-700",
  released: "tw-bg-emerald-100 tw-text-emerald-700",
};

const StageBadge = (params) => {
  const stage = params.value || "hidden";
  const cls = STAGE_BADGE[stage] || STAGE_BADGE.hidden;
  return (
    <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-sm tw-text-xs tw-font-semibold tw-capitalize ${cls}`}>
      {stage}
    </span>
  );
};

const columns = [
  { headerName: "Code",        field: "code",        minWidth: 100, sortable: false },
  { headerName: "Name",        field: "name",        minWidth: 260, sortable: false },
  { headerName: "Stage",       field: "stage",       minWidth: 100, sortable: false, cellRenderer: StageBadge },
  { headerName: "Description", field: "description", minWidth: 320, sortable: false },
];

const TABS = [{ key: "all", label: "All", Icon: MdFormatListBulleted, thunk: getAllFeatureFlags, dataKey: "allData" }];

const FeatureFlagsPage = () => {
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (flag) => { setEditing(flag); setShowForm(true); };

  const handleDelete = (rows, reload) => {
    confirmAlert({
      message: `Delete ${rows.length} feature flag${rows.length > 1 ? "s" : ""}? Without a flag, the code falls back to role permissions: only admins and roles granted that code will see the menu. To show a feature to everyone, set its stage to Released instead.`,
      onConfirm: async () => {
        await dispatch(deleteFeatureFlag(rows.map((r) => r.id)));
        reload?.();
      }
    });
  };

  const headerExtra = (
    <button
      type="button"
      onClick={openNew}
      className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-primary-600 tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-text-sm tw-font-medium tw-cursor-pointer"
    >
      <FaPlus size={11} /> New Feature Flag
    </button>
  );

  const renderSelectionActions = (rows, reload) => (
    <div className="tw-flex tw-items-center tw-gap-2 tw-mr-1">
      <span className="tw-text-xs tw-text-slate-500 tw-font-medium tw-whitespace-nowrap">{rows.length} selected</span>
      {rows.length === 1 && (
        <button
          type="button"
          onClick={() => openEdit(rows[0])}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer"
        >
          <FaEdit size={12} /> Edit
        </button>
      )}
      <button
        type="button"
        onClick={() => handleDelete(rows, reload)}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-red-300 tw-bg-red-50 tw-text-red-600 hover:tw-bg-red-100 tw-text-sm tw-font-medium tw-cursor-pointer"
      >
        <FaTrashAlt size={12} /> Delete
      </button>
    </div>
  );

  return (
    <>
      <CrmListPage
        title="Feature Flags"
        subtitle="Hide/show new features by stage (hidden / internal / released)."
        Icon={MdFlag}
        reducerSelector={(s) => s.authorization_feature_flag}
        tabs={TABS}
        columns={columns}
        searchParamKey="code_or_name_or_description_cont"
        searchPlaceholder="Code, name, description..."
        headerExtra={headerExtra}
        enableSelection
        renderSelectionActions={renderSelectionActions}
      />

      <FeatureFlagForm
        showModal={showForm}
        setshowModal={setShowForm}
        title={editing ? "Edit Feature Flag" : "New Feature Flag"}
        featureFlag={editing}
      />
    </>
  );
};

export default FeatureFlagsPage;
